import React, { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface IOSModalButton {
  text: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  loading?: boolean;
  disabled?: boolean;
}

interface IOSModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  showCloseButton?: boolean;
  footerContent?: ReactNode; // 保持向后兼容
  buttons?: IOSModalButton[]; // 新增：支持多个按钮配置
  error?: string; // 新增：统一错误处理
}

/**
 * iOS风格的模态框组件
 * 提供统一的模态框样式和动画效果
 */
export const IOSModal: React.FC<IOSModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = false,
  footerContent,
  buttons,
  error
}) => {
  // 处理背景点击
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 获取按钮样式
  const getButtonStyles = (variant: IOSModalButton['variant'], isFirst: boolean, isLast: boolean) => {
    const baseStyles = "flex-1 py-3 text-ios-body font-medium";
    const borderStyles = !isLast ? "border-r border-ios-gray5 dark:border-ios-dark-gray4" : "";

    switch (variant) {
      case 'primary':
        return `${baseStyles} text-ios-blue dark:text-ios-dark-blue ${borderStyles}`;
      case 'danger':
        return `${baseStyles} text-ios-red dark:text-ios-dark-red ${borderStyles}`;
      case 'secondary':
      default:
        return `${baseStyles} text-ios-gray dark:text-ios-dark-gray ${borderStyles}`;
    }
  };

  // 渲染按钮内容（处理加载状态）
  const renderButtonContent = (button: IOSModalButton) => {
    if (button.loading) {
      const spinnerColor = button.variant === 'primary'
        ? 'border-ios-blue/30 dark:border-ios-dark-blue/30 border-t-ios-blue dark:border-t-ios-dark-blue'
        : button.variant === 'danger'
        ? 'border-ios-red/30 dark:border-ios-dark-red/30 border-t-ios-red dark:border-t-ios-dark-red'
        : 'border-ios-gray/30 dark:border-ios-dark-gray/30 border-t-ios-gray dark:border-t-ios-dark-gray';

      return (
        <span className={`inline-block w-5 h-5 border-2 ${spinnerColor} rounded-full animate-spin mx-auto`}></span>
      );
    }
    return button.text;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/30 dark:bg-black/50"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-md mx-4 bg-white dark:bg-ios-dark-gray6 rounded-ios-lg shadow-ios dark:shadow-ios-dark overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 模态框标题 */}
            {title && (
              <div className="px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
                <h2 className="text-ios-title font-semibold text-center">{title}</h2>
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="absolute top-3 right-4 text-ios-gray dark:text-ios-dark-gray"
                    aria-label="关闭"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* 内容区域 */}
            <div className="px-4 py-4">
              {/* 错误提示 */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-ios-md text-ios-subhead">
                  {error}
                </div>
              )}
              {children}
            </div>

            {/* 底部区域 - 优先使用新的按钮配置，向后兼容 footerContent */}
            {buttons && buttons.length > 0 ? (
              <div className="flex border-t border-ios-gray5 dark:border-ios-dark-gray4">
                {buttons.map((button, index) => (
                  <button
                    key={index}
                    onClick={button.onClick}
                    disabled={button.disabled || button.loading}
                    className={getButtonStyles(button.variant, index === 0, index === buttons.length - 1)}
                  >
                    {renderButtonContent(button)}
                  </button>
                ))}
              </div>
            ) : footerContent ? (
              <div className="px-4 py-3 border-t border-ios-gray5 dark:border-ios-dark-gray4">
                {footerContent}
              </div>
            ) : null}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
